import React from 'react';
import styled, { useTheme } from 'styled-components';
import { ActionState, EPDModel } from 'services/EpdClient';
import { FixedLabelTop, FixedRow } from '../styles';
import { Validation } from '../Validation';
import { useTranslation } from 'react-i18next';
import { WizardStepValidationSchema } from '../types';
import { A, InfoBox } from 'styles/Styles.styled';
import { EPD_LCA_INDICATORS, EPD_LCA_SELF_CHECK_GUIDE_URL, EPD_LCA_TEMPLATES_URL } from 'constants/constants';
import { EpdLinks } from 'routes/EpdRoutes';
import UploadFile from 'components/form/UploadFile';
import { useMutation } from '@tanstack/react-query';
import EPDService from 'services/EpdService';
import { toaster } from 'components/v2/toast';
import WizardLcaResultsErrors from '../../../components/wizard/WizardLcaResultsErrors';
import isAxiosErrorWithLcaXLValidationErrors from 'util/isAxiosErrorWithLcaXLValidationErrors';
import { LcaXLValidationErrorCode } from 'types/lca-results/LcaXLValidationErrorCode';
import { LCAResultsOutdatedTemplateDialog } from 'components/v2/dialogs/LCAResultsOutdatedTemplateDialog';

interface Props {
  epd: EPDModel;
  onRemoveFile(fileId: any): void;
  setEpdModel(newEpd: EPDModel): void;
  validation: WizardStepValidationSchema;
}

const WizardStep2LcaResults: React.FC<Props> = ({ epd, onRemoveFile, setEpdModel, validation }) => {
  const { t } = useTranslation();
  const uploadLcaFileMutation = useMutation({
    mutationFn: (file: File) => EPDService.addLCAFile(epd.id!, file),
  });
  const theme = useTheme();

  const handleUploadFile = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const updatedEpd = await uploadLcaFileMutation.mutateAsync(file);
      setEpdModel(updatedEpd);
    } catch (e: any) {
      if (e?.response?.data.some((er: any) => er.code === LcaXLValidationErrorCode.INVALID_VALUE_MAX_LENGTH)) {
        toaster({
          severity: 'error',
          summary: 'Character limit exceeded in the uploaded file.',
          details: 'Please review the instructions, update the file, and try again.',
        });

        return;
      }

      if (e?.response?.data.some((er: any) => er.code === LcaXLValidationErrorCode.OUTDATED_TEMPLATE)) {
        LCAResultsOutdatedTemplateDialog();
        return;
      }

      if (e?.response?.data.some((er: any) => er.code === LcaXLValidationErrorCode.INVALID_TEMPLATE)) {
        toaster({
          severity: 'error',
          summary: "The file couldn't be processed",
          details: (
            <>
              Ensure that the file is created using the latest version of the template
              <br /> and try uploading it again.
            </>
          ),
        });

        return;
      }

      if (isAxiosErrorWithLcaXLValidationErrors(e)) {
        return;
      }
      console.error('Failed to upload LCA results file', e);
      toaster({
        severity: 'error',
        summary: 'Something went wrong while processing your file.',
        details: 'Please try again or contact support if the issue persists.',
      });
    }
  };

  let errorElement = null;
  if (isAxiosErrorWithLcaXLValidationErrors(uploadLcaFileMutation.error)) {
    errorElement = <WizardLcaResultsErrors errors={uploadLcaFileMutation.error.response!.data} style={{ marginTop: 16 }} />;
  }

  return (
    <FixedRow state={epd?.lcAsState.actionState}>
      <FixedLabelTop>
        {t('epdWizard.wizardStep2.properties.lcaResults')}
        <Validation validation={validation} epd={epd} field="lcAs" />
      </FixedLabelTop>
      <div style={{ width: '100%' }}>
        {epd?.lcaTemplateUrlState?.actionState !== ActionState.Hidden && (
          <InfoBox>
            <div>
              <div>Guidelines for uploading LCA results:</div>
              <UnorderedList>
                <li>
                  Download the Excel template to fill in your LCA results. Ensure that the{' '}
                  <strong>latest version of the "Excel template for PCR 2019:14"</strong> from{' '}
                  <A href={EPD_LCA_TEMPLATES_URL} target="_blank" rel="noopener noreferrer">
                    environdec.com
                  </A>{' '}
                  is used for construction products, as older versions are not accepted.
                </li>
                <li>
                  Refer to the latest{' '}
                  <A href={EPD_LCA_INDICATORS} target="_blank" rel="noopener noreferrer">
                    characterisation factors on our website
                  </A>{' '}
                  for accurate data entry.
                </li>
                <li>
                  Use{' '}
                  <A href={EPD_LCA_SELF_CHECK_GUIDE_URL} target="_blank" rel="noopener noreferrer">
                    the self-check guide
                  </A>{' '}
                  to troubleshoot and correct issues if your LCA results file is declined.
                </li>
              </UnorderedList>
            </div>
          </InfoBox>
        )}
        {epd?.lcAsState?.actionState === ActionState.Locked && (
          <div style={{ backgroundColor: theme.colors.lightOrange }}>
            This section is locked for maintenance or other reasons by admin.
          </div>
        )}
        {epd?.isEn15804Compliant && (
          <InfoBox>
            <div>
              <div>{t('epdWizard.wizardStep2.info.lcaValidateText')}</div>
              <div className="mt-2">
                <A href={EpdLinks.lcaValidation()} target="blank" rel="noopener">
                  {t('epdWizard.wizardStep2.info.lcaValidate')}
                </A>
              </div>
            </div>
          </InfoBox>
        )}

        <UploadFile
          accept=".xlsx"
          title={t('epdWizard.wizardStep2.upload.lcaResults')}
          uploadFile={handleUploadFile}
          disabled={
            epd?.lcAsState?.actionState === ActionState.Locked || epd?.lcAsState?.actionState === ActionState.Disabled
          }
          uploadDisabled={(epd?.lcAs?.length ?? 0) > 0 || uploadLcaFileMutation.isPending}
          documents={epd?.lcAs}
          onRemoveFile={onRemoveFile}
          field="LCA"
          showUploadingIndicator={uploadLcaFileMutation.isPending}
          footerNode={errorElement}
          singleFile
        />
      </div>
    </FixedRow>
  );
};

export default WizardStep2LcaResults;

const UnorderedList = styled.ul`
  margin: 0;
  line-height: 20px;
  margin-top: 0.5em;
  padding-left: 1.75em;
`;
